import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { AchievementItem } from '@/mocks/achievements';
import { RailwayTiesPattern, RailwayCrosshatch } from '@/components/feature/RailwayPatterns';

interface AchievementShowcaseProps {
  achievement: AchievementItem;
}

// ── Video Player Component ──
function VideoPlayer({ poster, videoUrl }: { poster: string; videoUrl: string }) {
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative rounded-lg overflow-hidden h-[320px] md:h-[420px] cursor-pointer group/video"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setPlaying(!playing)}
    >
      {/* Poster image */}
      <img
        src={poster}
        alt="成果视频封面"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
          playing ? 'opacity-0 scale-110' : 'opacity-100 scale-100'
        }`}
      />

      {/* Dark overlay when not playing */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/15 transition-opacity duration-500 pointer-events-none ${
          playing ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Play button overlay */}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all duration-500 ${
              hovered ? 'scale-110' : 'scale-100'
            }`}
            style={{
              background: 'rgba(189,1,1,0.85)',
              boxShadow: hovered
                ? '0 0 48px rgba(189,1,1,0.55)'
                : '0 0 28px rgba(189,1,1,0.3)',
            }}
          >
            <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1" />
          </div>
        </div>
      )}

      {/* Video label top-left */}
      <div
        className={`absolute top-4 left-4 z-10 transition-all duration-500 ${
          playing ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <span className="text-white/80 text-xs bg-black/35 px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5">
          <i className="ri-video-line" />
          成果视频
        </span>
      </div>

      {/* Duration badge bottom-right */}
      {!playing && (
        <div className="absolute bottom-4 right-4 z-10">
          <span className="text-white/70 text-xs bg-black/35 px-2.5 py-1 rounded-full backdrop-blur-sm">
            08:24
          </span>
        </div>
      )}

      {/* Playing state — simulated video */}
      {playing && (
        <div className="absolute inset-0 bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-500/20 flex items-center justify-center animate-pulse">
              <i className="ri-video-line text-primary-400 text-2xl" />
            </div>
            <p className="text-white/60 text-sm">成果视频播放中</p>
            <p className="text-white/30 text-xs mt-1">点击暂停</p>
          </div>
        </div>
      )}

      {/* Progress bar */}
      {playing && (
        <div className="absolute bottom-4 left-4 right-4 z-10">
          <div className="h-1 bg-white/15 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500"
              style={{
                width: '37%',
                animation: 'videoProgress 8s linear infinite',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Showcase Component ──
export default function AchievementShowcase({ achievement }: AchievementShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !visible) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  const { videoPoster, videoUrl } = achievement.detail.homepage;

  return (
    <section id="achievement-showcase" className="relative pt-14 md:pt-20 pb-4 md:pb-6 px-4 md:px-8 bg-background-50 overflow-hidden">
      {/* ━━━ Impact Shockwave Ring ━━━ */}
      {visible && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div
            className="absolute rounded-full border-2 border-primary-500/40"
            style={{
              width: '60px',
              height: '60px',
              animation: 'shockwaveRing 3.0s ease-out infinite',
            }}
          />
          <div
            className="absolute rounded-full border-2 border-primary-400/25"
            style={{
              width: '60px',
              height: '60px',
              animation: 'shockwaveRing 3.0s ease-out 1.0s infinite',
            }}
          />
          <div
            className="absolute rounded-full border border-accent-400/20"
            style={{
              width: '60px',
              height: '60px',
              animation: 'shockwaveRing 3.0s ease-out 2.0s infinite',
            }}
          />
        </div>
      )}

      {/* ━━━ Sweeping Beam ━━ */}
      {visible && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-0 bottom-0"
            style={{
              width: '80px',
              background: 'linear-gradient(90deg, transparent, rgba(189,1,1,0.06), transparent)',
              animation: 'beamSweep 3.5s ease-out infinite',
            }}
          />
        </div>
      )}

      <div className="max-w-[1200px] mx-auto" ref={sectionRef}>
        {/* ═══════════════════════════════════════
            TWO-COLUMN LAYOUT: 成果简介 | 成果视频
            ═══════════════════════════════════════ */}
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8 mb-8 md:mb-10">
          {/* ━━ LEFT: 成果简介 ━━ */}
          <div
            className="relative flex-1"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0) scale(1)' : 'translateX(-60px) scale(0.9)',
              filter: visible ? 'blur(0)' : 'blur(3px)',
              transition: 'opacity 1.0s cubic-bezier(0.16,1,0.22,1) 0.15s, transform 1.0s cubic-bezier(0.16,1,0.22,1) 0.15s, filter 1.0s cubic-bezier(0.16,1,0.22,1) 0.15s',
            }}
          >
            {/* ── BLEED: offset accent frame ── */}
            <div className="absolute inset-0 translate-x-[10px] translate-y-[10px] rounded-lg border border-accent-400/40 pointer-events-none hidden md:block" />
            {/* ── BLEED: bold accent rail ── */}
            <div className="absolute left-0 -top-3 -bottom-3 w-[5px] rounded-full bg-accent-500 z-20 pointer-events-none" />

            <div className="relative bg-primary-500 rounded-lg overflow-hidden h-full flex flex-col pl-[5px]">
            {/* Faint white rail texture */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.06]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.05]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />

            <div className="relative z-10 p-6 md:p-8 lg:p-9 flex-1 flex flex-col">
            {/* ── Header: label + number synced style ── */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="block font-heading font-black text-[32px] md:text-[38px] leading-none text-white tracking-tight">
                  成果简介
                </span>
                <span className="text-[10px] text-white/40 font-medium tracking-[0.18em] uppercase">Achievement Overview</span>
              </div>
              <span className="block font-heading font-black text-[72px] md:text-[88px] leading-none text-white/12 select-none pointer-events-none">
                01
              </span>
            </div>

            {/* ── Title ── */}
            <Link
              to={`/achievement/${achievement.id}`}
              className="block group/title cursor-pointer mb-7"
            >
              <h3
                className="text-[24px] md:text-[30px] font-bold font-heading text-white/80 leading-[1.3] transition-colors duration-500 group-hover/title:text-accent-200"
                style={{ letterSpacing: '0.01em' }}
              >
                {achievement.name}
              </h3>
            </Link>

            {/* ── Metadata row ── */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-7 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-medal-line text-accent-400 text-[14px]" />
                <span className="font-medium text-white/85">{achievement.category}</span>
              </span>
              <span className="text-white/20 select-none">|</span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-check-double-line text-accent-400 text-[14px]" />
                <span className="font-medium text-white/85">{achievement.status}</span>
              </span>
              <span className="text-white/20 select-none">|</span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-white/50 text-[14px]" />
                <span className="font-medium text-white/70">{achievement.year}</span>
              </span>
            </div>

            {/* ── Summary ── */}
            <div className="relative bg-white/[0.08] rounded-lg border-l-2 border-accent-500 pl-5 pr-5 py-4">
              <p className="text-white/80 text-sm md:text-[15px] leading-[1.8] max-w-xl">
                {achievement.detail.homepage.summary}
              </p>
            </div>
            </div>
            </div>
          </div>

          {/* ━━ RIGHT: 成果视频 ━━ */}
          <div
            className="relative flex-1"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0) scale(1)' : 'translateX(60px) scale(0.9)',
              filter: visible ? 'blur(0)' : 'blur(3px)',
              transition: 'opacity 1.0s cubic-bezier(0.16,1,0.22,1) 0.35s, transform 1.0s cubic-bezier(0.16,1,0.22,1) 0.35s, filter 1.0s cubic-bezier(0.16,1,0.22,1) 0.35s',
            }}
          >
            {/* ── BLEED: offset accent frame ── */}
            <div className="absolute inset-0 translate-x-[10px] translate-y-[10px] rounded-lg border border-primary-400/30 pointer-events-none hidden md:block" />
            {/* ── BLEED: bold accent rail ── */}
            <div className="absolute left-0 -top-3 -bottom-3 w-[5px] rounded-full bg-primary-400 z-20 pointer-events-none" />

            <div className="relative bg-primary-500 rounded-lg overflow-hidden h-full flex flex-col pl-[5px]">
            {/* Faint white rail texture */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.06]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.05]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />

            <div className="relative z-10 p-6 md:p-8 lg:p-9 flex-1 flex flex-col">
            {/* ── Header: label + number synced style ── */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="block font-heading font-black text-[32px] md:text-[38px] leading-none text-white tracking-tight">
                  成果视频
                </span>
                <span className="text-[10px] text-white/40 font-medium tracking-[0.18em] uppercase">Achievement Video</span>
              </div>
              <span className="block font-heading font-black text-[72px] md:text-[88px] leading-none text-white/12 select-none pointer-events-none">
                02
              </span>
            </div>

            <div>
              <VideoPlayer poster={videoPoster} videoUrl={videoUrl} />
            </div>
            </div>
            </div>
          </div>
        </div>


      </div>
    </section>
  );
}